import { supabase } from './supabaseService';
import { logger } from '../utils/logger';

export interface ServiceConnection {
  id: string;
  serviceName: 'qonto' | 'google' | 'openai' | 'firebase' | 'sendgrid' | 'fathom' | 'resend' | 'grafana';
  apiKey?: string;
  configData: Record<string, any>;
  isActive: boolean;
  lastConnectedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
}

export interface ConnectionLog {
  id: string;
  serviceName: string;
  eventType: 'connect' | 'disconnect' | 'error' | 'sync' | 'test' | 'migration';
  success: boolean;
  errorMessage?: string;
  metadata: Record<string, any>;
  createdAt: Date;
}

class ServiceConnectionsService {
  private cache: Map<string, ServiceConnection> = new Map();
  private cacheExpiry: Map<string, number> = new Map();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutos

  /**
   * Obtiene la configuración de un servicio desde la base de datos
   */
  async getServiceConnection(serviceName: string): Promise<ServiceConnection | null> {
    try {
      // Check cache first
      const cached = this.getFromCache(serviceName);
      if (cached) {
        logger.info(`Configuración de ${serviceName} obtenida desde caché`);
        return cached;
      }

      logger.debug(`Cargando configuración de ${serviceName} desde BD...`);

      // Try the base table first (admin/superadmin only per RLS as of
      // migration 20260917150000). Non-admins get 0 rows here and we fall
      // back to service_connections_meta which returns metadata sin api_key.
      const { data, error } = await supabase
        .from('service_connections')
        .select('*')
        .eq('service_name', serviceName)
        .maybeSingle();

      if (error) {
        logger.error(`Error cargando ${serviceName}:`, error);
        return null;
      }

      let connectionRow: any = data;
      if (!connectionRow) {
        const { data: metaData } = await supabase
          .from('service_connections_meta')
          .select('*')
          .eq('service_name', serviceName)
          .maybeSingle();
        if (!metaData) {
          logger.info(`No existe configuración para ${serviceName} en BD`);
          return null;
        }
        // Metadata-only response: api_key is intentionally null for non-admins.
        connectionRow = { ...metaData, api_key: null };
      }

      const connection: ServiceConnection = {
        id: connectionRow.id,
        serviceName: connectionRow.service_name,
        apiKey: connectionRow.api_key,
        configData: connectionRow.config_data || {},
        isActive: connectionRow.is_active,
        lastConnectedAt: connectionRow.last_connected_at ? new Date(connectionRow.last_connected_at) : undefined,
        createdAt: new Date(connectionRow.created_at),
        updatedAt: new Date(connectionRow.updated_at),
        createdBy: connectionRow.created_by
      };

      // Store in cache
      this.setInCache(serviceName, connection);

      logger.info(`Configuración de ${serviceName} cargada desde BD`);
      return connection;
    } catch (error) {
      logger.error(`Error obteniendo configuración de ${serviceName}:`, error);
      return null;
    }
  }

  /**
   * Guarda o actualiza la configuración de un servicio
   */
  async saveServiceConnection(
    serviceName: string,
    apiKey: string | undefined,
    configData: Record<string, any>,
    isActive: boolean = true
  ): Promise<ServiceConnection | null> {
    try {
      logger.debug(`Guardando configuración de ${serviceName} en BD...`);

      const { data: currentUser } = await supabase.auth.getUser();
      const userId = currentUser?.user?.id;

      const upsertData = {
        service_name: serviceName,
        api_key: apiKey,
        config_data: configData,
        is_active: isActive,
        created_by: userId,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('service_connections')
        .upsert(upsertData, { onConflict: 'service_name' })
        .select()
        .single();

      if (error) {
        logger.error(`Error guardando ${serviceName}:`, error);
        await this.logConnectionEvent(serviceName, 'sync', false, error.message);
        throw error;
      }

      const connection: ServiceConnection = {
        id: data.id,
        serviceName: data.service_name,
        apiKey: data.api_key,
        configData: data.config_data || {},
        isActive: data.is_active,
        lastConnectedAt: data.last_connected_at ? new Date(data.last_connected_at) : undefined,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
        createdBy: data.created_by
      };

      // Update cache
      this.setInCache(serviceName, connection);

      // Log success
      await this.logConnectionEvent(serviceName, 'sync', true, undefined, {
        hasApiKey: !!apiKey,
        configKeys: Object.keys(configData)
      });

      logger.info(`Configuración de ${serviceName} guardada en BD`);
      return connection;
    } catch (error) {
      logger.error(`Error guardando configuración de ${serviceName}:`, error);
      return null;
    }
  }

  /**
   * Actualiza el timestamp de última conexión exitosa
   */
  async updateLastConnected(serviceName: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('service_connections')
        .update({
          last_connected_at: new Date().toISOString(),
          is_active: true
        })
        .eq('service_name', serviceName);

      if (error) {
        logger.error(`Error actualizando last_connected_at para ${serviceName}:`, error);
        return;
      }

      // Clear cache to force reload
      this.clearFromCache(serviceName);

      logger.info(`Last connected actualizado para ${serviceName}`);
    } catch (error) {
      logger.error(`Error en updateLastConnected para ${serviceName}:`, error);
    }
  }

  /**
   * Marca un servicio como inactivo
   */
  async markAsInactive(serviceName: string, reason?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('service_connections')
        .update({ is_active: false })
        .eq('service_name', serviceName);

      if (error) {
        logger.error(`Error marcando ${serviceName} como inactivo:`, error);
        return;
      }

      // Log disconnection
      await this.logConnectionEvent(serviceName, 'disconnect', true, reason);

      // Clear cache
      this.clearFromCache(serviceName);

      console.log(`⚠️ ${serviceName} marcado como inactivo`);
    } catch (error) {
      logger.error(`Error en markAsInactive para ${serviceName}:`, error);
    }
  }

  /**
   * Obtiene todas las configuraciones de servicios
   */
  async getAllServiceConnections(): Promise<ServiceConnection[]> {
    try {
      logger.debug('Cargando todas las configuraciones de servicios...');

      const { data, error } = await supabase
        .from('service_connections')
        .select('*')
        .order('service_name', { ascending: true });

      if (error) {
        logger.error('Error cargando todas las configuraciones', error);
        return [];
      }

      const connections: ServiceConnection[] = (data || []).map(item => ({
        id: item.id,
        serviceName: item.service_name,
        apiKey: item.api_key,
        configData: item.config_data || {},
        isActive: item.is_active,
        lastConnectedAt: item.last_connected_at ? new Date(item.last_connected_at) : undefined,
        createdAt: new Date(item.created_at),
        updatedAt: new Date(item.updated_at),
        createdBy: item.created_by
      }));

      logger.info(`${connections.length} configuraciones cargadas`);
      return connections;
    } catch (error) {
      logger.error('Error obteniendo todas las configuraciones', error);
      return [];
    }
  }

  /**
   * Registra un evento de conexión en los logs
   */
  async logConnectionEvent(
    serviceName: string,
    eventType: 'connect' | 'disconnect' | 'error' | 'sync' | 'test' | 'migration',
    success: boolean,
    errorMessage?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('connection_logs')
        .insert({
          service_name: serviceName,
          event_type: eventType,
          success,
          error_message: errorMessage,
          metadata: metadata || {}
        });

      if (error) {
        console.error(`⚠️ Error registrando log para ${serviceName}:`, error);
      }
    } catch (error) {
      console.error(`⚠️ Error en logConnectionEvent:`, error);
    }
  }

  /**
   * Obtiene los logs de conexión de un servicio
   */
  async getConnectionLogs(
    serviceName?: string,
    limit: number = 50
  ): Promise<ConnectionLog[]> {
    try {
      let query = supabase
        .from('connection_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (serviceName) {
        query = query.eq('service_name', serviceName);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('Error cargando logs de conexión', error);
        return [];
      }

      return (data || []).map(item => ({
        id: item.id,
        serviceName: item.service_name,
        eventType: item.event_type,
        success: item.success,
        errorMessage: item.error_message,
        metadata: item.metadata || {},
        createdAt: new Date(item.created_at)
      }));
    } catch (error) {
      logger.error('Error obteniendo logs de conexión', error);
      return [];
    }
  }

  /**
   * Migra configuración desde localStorage a base de datos
   */
  async migrateFromLocalStorage(serviceName: string, legacyConfig: any): Promise<boolean> {
    try {
      logger.debug(`Migrando ${serviceName} desde localStorage a BD...`);

      let apiKey: string | undefined;
      let configData: Record<string, any> = {};

      // Extract service-specific data based on service name
      switch (serviceName) {
        case 'qonto':
          apiKey = legacyConfig.qontoSecretKey;
          configData = {
            login: legacyConfig.qontoLogin,
            organizationSlug: legacyConfig.qontoOrganizationSlug,
            isConfigured: legacyConfig.qontoIsConfigured
          };
          break;
        case 'google':
          apiKey = legacyConfig.googleClientSecret;
          configData = {
            clientId: legacyConfig.googleClientId,
            redirectUri: legacyConfig.googleRedirectUri
          };
          break;
        case 'openai':
          apiKey = legacyConfig.openaiApiKey;
          configData = {
            organizationId: legacyConfig.openaiOrganizationId,
            model: legacyConfig.openaiModel || 'gpt-4',
            maxTokens: legacyConfig.openaiMaxTokens || 1000,
            temperature: legacyConfig.openaiTemperature || 0.7,
            isConfigured: legacyConfig.openaiIsConfigured
          };
          break;
        case 'firebase':
          apiKey = legacyConfig.firebaseApiKey;
          configData = {
            authDomain: legacyConfig.firebaseAuthDomain,
            projectId: legacyConfig.firebaseProjectId,
            storageBucket: legacyConfig.firebaseStorageBucket,
            messagingSenderId: legacyConfig.firebaseMessagingSenderId,
            appId: legacyConfig.firebaseAppId,
            vapidKey: legacyConfig.firebaseVapidKey
          };
          break;
        case 'sendgrid':
          apiKey = legacyConfig.sendgridApiKey;
          configData = {};
          break;
        case 'fathom':
          apiKey = legacyConfig.fathomApiKey;
          configData = {
            webhookSecret: legacyConfig.fathomWebhookSecret,
            useOAuth: legacyConfig.fathomUseOAuth,
            oauthToken: legacyConfig.fathomOAuthToken,
            oauthRefreshToken: legacyConfig.fathomOAuthRefreshToken,
            oauthExpiresAt: legacyConfig.fathomOAuthExpiresAt,
            webhookUrl: legacyConfig.fathomWebhookUrl,
            isConfigured: legacyConfig.fathomIsConfigured
          };
          break;
        case 'resend':
          apiKey = legacyConfig.resendApiKey;
          configData = {
            fromEmail: legacyConfig.resendFromEmail,
            fromName: legacyConfig.resendFromName,
            isConfigured: legacyConfig.resendIsConfigured
          };
          break;
        case 'grafana':
          apiKey = undefined;
          configData = {
            url: legacyConfig.grafanaUrl,
            api_key: legacyConfig.grafanaApiKey,
            loki_url: legacyConfig.grafanaLokiUrl,
            dashboard_id: legacyConfig.grafanaDashboardId,
            enabled: legacyConfig.grafanaEnabled,
            isConfigured: legacyConfig.grafanaIsConfigured
          };
          break;
      }

      if (!apiKey && Object.keys(configData).length === 0) {
        logger.info(`No hay datos para migrar para ${serviceName}`);
        return false;
      }

      const result = await this.saveServiceConnection(serviceName, apiKey, configData);

      if (result) {
        await this.logConnectionEvent(serviceName, 'migration', true, undefined, {
          source: 'localStorage',
          migratedFields: Object.keys(configData)
        });
        logger.info(`${serviceName} migrado exitosamente`);
        return true;
      }

      return false;
    } catch (error) {
      logger.error(`Error migrando ${serviceName}:`, error);
      await this.logConnectionEvent(serviceName, 'migration', false, String(error));
      return false;
    }
  }

  /**
   * Migra todas las configuraciones desde localStorage
   */
  async migrateAllFromLocalStorage(): Promise<{ success: number; failed: number }> {
    try {
      logger.debug('Iniciando migración completa desde localStorage...');

      const legacyConfigStr = localStorage.getItem('crm_configuration');
      if (!legacyConfigStr) {
        logger.info('No hay configuración legacy para migrar');
        return { success: 0, failed: 0 };
      }

      const legacyConfig = JSON.parse(legacyConfigStr);
      const services: Array<'qonto' | 'google' | 'openai' | 'firebase' | 'sendgrid' | 'fathom' | 'resend' | 'grafana'> = [
        'qonto', 'google', 'openai', 'firebase', 'sendgrid', 'fathom', 'resend', 'grafana'
      ];

      let success = 0;
      let failed = 0;

      for (const service of services) {
        const result = await this.migrateFromLocalStorage(service, legacyConfig);
        if (result) {
          success++;
        } else {
          failed++;
        }
      }

      logger.info(`Migración completa: ${success} exitosos, ${failed} fallidos`);
      return { success, failed };
    } catch (error) {
      logger.error('Error en migración completa', error);
      return { success: 0, failed: 0 };
    }
  }

  // Cache management
  private getFromCache(serviceName: string): ServiceConnection | null {
    const expiry = this.cacheExpiry.get(serviceName);
    if (expiry && Date.now() < expiry) {
      return this.cache.get(serviceName) || null;
    }
    // Cache expired
    this.clearFromCache(serviceName);
    return null;
  }

  private setInCache(serviceName: string, connection: ServiceConnection): void {
    this.cache.set(serviceName, connection);
    this.cacheExpiry.set(serviceName, Date.now() + this.CACHE_TTL);
  }

  private clearFromCache(serviceName: string): void {
    this.cache.delete(serviceName);
    this.cacheExpiry.delete(serviceName);
  }

  clearAllCache(): void {
    this.cache.clear();
    this.cacheExpiry.clear();
    logger.debug('Cache de conexiones limpiado');
  }
}

export const serviceConnectionsService = new ServiceConnectionsService();
